import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  integrations_monitored: number
  healthy_integrations: number
  degraded_integrations: number
  failed_integrations: number
  avg_response_time_ms: number
  uptime_pct: number
}

interface IntegrationHealth {
  name: string
  status: string
  uptime: number
  latency_ms: number
  errors_24h: number
  last_check: string
}

interface Alert {
  id: string
  integration: string
  type: string
  severity: string
  timestamp: string
}

export default function IntegrationMonitoringPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [health, setHealth] = useState<IntegrationHealth[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, healthRes, alertsRes] = await Promise.all([
          fetch('/api/integrationmonitoring/statistics'),
          fetch('/api/integrationmonitoring/health'),
          fetch('/api/integrationmonitoring/alerts')
        ])
        setStatistics(await statsRes.json())
        setHealth(await healthRes.json())
        setAlerts(await alertsRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Healthy': 'bg-green-100 text-green-800',
      'Degraded': 'bg-yellow-100 text-yellow-800',
      'Failed': 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const getSeverityBadge = (severity: string) => {
    const colors: Record<string, string> = {
      'Critical': 'bg-red-100 text-red-800',
      'Warning': 'bg-yellow-100 text-yellow-800',
      'Info': 'bg-blue-100 text-blue-800'
    }
    return colors[severity] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Integration Monitoring...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Integration Monitoring</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.integrations_monitored}</div>
              <div className="text-sm text-gray-500">Monitored</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.healthy_integrations}</div>
              <div className="text-sm text-gray-500">Healthy</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-yellow-600">{statistics.degraded_integrations}</div>
              <div className="text-sm text-gray-500">Degraded</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-red-600">{statistics.failed_integrations}</div>
              <div className="text-sm text-gray-500">Failed</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.avg_response_time_ms}ms</div>
              <div className="text-sm text-gray-500">Avg Response</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.uptime_pct}%</div>
              <div className="text-sm text-gray-500">Uptime</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Integration Health</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Integration</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Uptime</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Latency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Errors (24h)</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {health.map((h, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{h.name}</td>
                      <td className="px-4 py-3 text-gray-600">{h.uptime}%</td>
                      <td className="px-4 py-3 text-gray-600">{h.latency_ms}ms</td>
                      <td className="px-4 py-3 text-gray-600">{h.errors_24h}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(h.status)}`}>
                          {h.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Recent Alerts</h2></div>
            <div className="p-4 space-y-3">
              {alerts.map((alert) => (
                <div key={alert.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{alert.integration}</span>
                    <span className={`px-2 py-1 rounded-full text-xs ${getSeverityBadge(alert.severity)}`}>
                      {alert.severity}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">{alert.type}</div>
                  <div className="text-xs text-gray-400 mt-1">{alert.timestamp}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
